import { EAuthStatus } from './enums'
import { COOKIE_AUTH_TOKEN, COOKIE_EXPIRES_TOKEN } from 'constants/common'
import { ERROR_CODE } from 'constants/common'
import { ERole } from 'enums'
import { IClinicInfo } from 'interfaces'
import { IResponse } from 'interfaces/services'
import Cookies from 'js-cookie'
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { ROUTES_GROUP } from 'routes'
import { ERouteName, IRoute } from 'routes/IRoute'
import { useAuthServices, useClinicServices } from 'services'
import useResetStore from 'store/useResetStore'
import { removeToken, saveToken } from 'utils'

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const AuthContext = createContext<AuthProps>(null!)

type AuthStatus = 'authorized' | 'unauthorized'

export interface AuthProps {
  status: AuthStatus
  userId: number
  manyClinics: any
  activeUser: boolean
  activeRole: boolean
  isSysAdmin: boolean
  userProfile: IClinicInfo | null
  routes: Array<IRoute>
  isAuth: boolean
  onLogout: () => void
  onLogin: (data: any) => void
  signOut?: () => void
  reloadUserProfile?: () => Promise<IClinicInfo | undefined>
  setUserProfile?: (data: IClinicInfo) => void
}

export const getToken = () => {
  return Cookies.get(COOKIE_AUTH_TOKEN)
}

export const getExpiresToken = () => {
  return Cookies.get(COOKIE_EXPIRES_TOKEN)
}

export const useAuth = () => {
  return useContext(AuthContext)
}

export const checkAuth = () => {
  const token = getToken()
  return !!token
}

export const AuthProvider: FC = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isAuth = checkAuth()
  const { login, logout } = useAuthServices()
  const { getClinicInfo } = useClinicServices()
  const [manyClinics, isManyClinics] = useState<[]>()
  const [status, setStatus] = useState<AuthStatus>('unauthorized')
  const [userProfile, setUserProfile] = useState<any | null>(null)
  const [routes, setRoutes] = useState<Array<IRoute>>([])
  const [activeUser, isActiveUser] = useState<boolean>(false)
  const [activeRole, isActiveRole] = useState<boolean>(false)
  const [isSysAdmin, checkSysAdmin] = useState<boolean>(false)
  const { resetStore } = useResetStore()
  useEffect(() => {
    const role = ERole.Development
    getRoutesWithRole(role)
  }, [userProfile])
  useEffect(() => {
    if (userProfile?.my_clinic_user?.status === EAuthStatus.Active) {
      isActiveUser(true)
    }
    if (
      userProfile?.my_clinic_user?.clinic_role?.status === EAuthStatus.Active
    ) {
      isActiveRole(true)
    }
    if (
      userProfile?.my_clinic_user?.clinic_role?.role.toLowerCase().trim() ===
      'sysadmin'
    ) {
      checkSysAdmin(true)
    }
  }, [userProfile])
  useEffect(() => {
    if (isAuth) {
      setStatus('authorized')
    } else {
      setStatus('unauthorized')
    }
  }, [isAuth, status, location.pathname])

  useEffect(() => {
    getUserProfile()
  }, [])
  const hasPermission = (permissions: any, category: string) =>
    permissions?.[category] &&
    Object.values(permissions[category]).some(Boolean)

  const getPermissionWithRole = (role: ERole) => {
    let UserRole
    if (
      userProfile?.my_clinic_user?.clinic_role?.status === EAuthStatus.Active
    ) {
      UserRole = userProfile?.my_clinic_user?.clinic_role?.permission
    } else UserRole = {}
    const permissions = {
      hasAccountManagement: hasPermission(UserRole, 'account_management'),
      hasCategoryManagement: hasPermission(UserRole, 'category_management'),
      hasExamination: hasPermission(UserRole, 'examination'),
      hasPatientManagement: hasPermission(UserRole, 'patient_management'),
      hasWarehouseManagement: hasPermission(UserRole, 'warehouse_management')
    }

    const routesMap = {
      [ERole.Admin]: Object.values(ERouteName),
      [ERole.Development]: [
        // Account and role
        permissions.hasAccountManagement && ERouteName.AccountManagementGroup,
        UserRole?.account_management?.clinic_info &&
          ERouteName.AccountManagement_ClinicInfo,
        UserRole?.account_management?.manage_user &&
          ERouteName.AccountManagement_User,
        UserRole?.account_management?.permission &&
          ERouteName.AccountManagement_Role,
        UserRole?.account_management?.system_config &&
          ERouteName.AccountManagement_SystemSetting,
        UserRole?.account_management?.system_config &&
          ERouteName.AccountManagement_SystemSettingAdd,
        UserRole?.account_management?.system_config &&
          ERouteName.AccountManagement_SystemSettingDetail,
        // Patient
        permissions.hasPatientManagement && ERouteName.PatientManagementGroup,
        UserRole?.patient_management?.booking &&
          ERouteName.PatientManagement_Booking,
        UserRole?.patient_management?.transaction &&
          ERouteName.PatientManagement_Transaction,
        UserRole?.patient_management?.patient &&
          ERouteName.PatientManagement_Patient,
        UserRole?.patient_management?.patient &&
          ERouteName.PatientManagement_PatientDetail,
        UserRole?.patient_management?.booking &&
          ERouteName.PatientManagement_ReceiveDetail,
        UserRole?.patient_management?.booking &&
          ERouteName.PatientManagement_ReceiveAdd,
        UserRole?.patient_management?.booking &&
          ERouteName.PatientManagement_Receive,
        UserRole?.patient_management?.payment &&
          ERouteName.PatientManagement_PaymentDetail,
        UserRole?.patient_management?.payment &&
          ERouteName.PatientManagement_Payment,
        // Menu
        permissions.hasCategoryManagement && ERouteName.MenuManagementGroup,
        UserRole?.category_management?.clinic &&
          ERouteName.MenuManagement_ClinicRoomSetting,
        UserRole?.category_management?.doctor &&
          ERouteName.MenuManagement_Doctor,
        UserRole?.category_management?.group &&
          ERouteName.MenuManagement_GroupService,
        UserRole?.category_management?.services &&
          ERouteName.MenuManagement_SubService,
        UserRole?.category_management?.timing &&
          ERouteName.MenuManagement_TimeSetting,
        // Examination
        permissions.hasExamination && ERouteName.ExamGroup,
        UserRole?.examination?.examination && ERouteName.ExamGroup_Exam_Patient,
        UserRole?.examination?.examination && ERouteName.ExamGroup_Exam,
        // Repo
        permissions.hasWarehouseManagement && ERouteName.RepoManagementGroup,
        UserRole?.warehouse_management?.goods_receipt &&
          ERouteName.RepoManagement_Import,
        UserRole?.warehouse_management?.inventory &&
          ERouteName.RepoManagement_Inventory,
        // Help
        ERouteName.HelpGroup,
        ERouteName.HelpGroupUserManual,
        ERouteName.HelpGroupContact
      ],
      [ERole.Doctor]: [
        permissions.hasAccountManagement && ERouteName.AccountManagementGroup,
        UserRole?.account_management?.clinic_info &&
          ERouteName.AccountManagement_ClinicInfo,
        permissions.hasCategoryManagement && ERouteName.MenuManagementGroup,
        UserRole?.category_management?.clinic &&
          ERouteName.MenuManagement_ClinicRoomSetting
      ]
    }
    return (
      (userProfile?.my_clinic_user?.status === EAuthStatus.Active &&
        routesMap[role]?.filter(Boolean)) || [
        ERouteName.HelpGroup,
        ERouteName.HelpGroupUserManual,
        ERouteName.HelpGroupContact
      ]
    )
  }

  const getRoutesWithRole = (role: ERole) => {
    const permission = getPermissionWithRole(role)

    const checkPermissionRoute = (routes: Array<IRoute>) => {
      routes?.forEach((route) => {
        const hasAccess = permission.includes(route.name)
        route.hasAccess = hasAccess
        if (route?.children) {
          checkPermissionRoute(route.children)
        }
      })
    }

    checkPermissionRoute(ROUTES_GROUP)

    setRoutes(ROUTES_GROUP)
    return ROUTES_GROUP
  }

  const getUserProfile = async () => {
    try {
      const profile = await getClinicInfo()
      if (!profile?.data.id) {
        throw new Error('')
      }
      setUserProfile(profile.data)
      return profile.data
    } catch (err) {
      // localStorage.removeItem(COOKIE_AUTH_TOKEN)
      // localStorage.removeItem(COOKIE_EXPIRES_TOKEN)
    }
  }

  const signOut = useCallback(async () => {
    resetStore()
    removeToken()
    localStorage.removeItem('clinic_id')
    localStorage.removeItem('userIdOfClinic')
    window.location.assign('/login')
    setStatus('authorized')
    try {
      const response: any = await logout()
      if (response._errorCode !== ERROR_CODE.SUCCESS) {
        throw new Error('')
      }
    } catch (error) {}
  }, [navigate])

  const signIn = useCallback(
    async (data) => {
      const response: any = await login(data)
      handleResponseLogin(response)
      if (!response?.data?.clinics) {
        localStorage.setItem('clinic_id', response?.data?.token?.clinic_id)
      }
    },
    [navigate]
  )

  const handleResponseLogin = (response: IResponse<any>) => {
    if (response._errorCode !== ERROR_CODE.SUCCESS) {
      return
    }
    saveToken(response?.data?.token)
    if (response?.data?.clinics) {
      isManyClinics(response?.data?.clinics)
    } else {
      setStatus('authorized')
      window.location.assign('/')
    }
  }

  const value = {
    status,
    userProfile,
    userId: userProfile?.user?.user_id,
    routes,
    activeUser,
    activeRole,
    isSysAdmin,
    manyClinics,
    isAuth: status === 'authorized' && !manyClinics,
    onLogout: signOut,
    onLogin: signIn,
    reloadUserProfile: getUserProfile,
    setUserProfile
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const ProtectedRoute: FC = ({ children }) => {
  const { status } = useAuth()
  const location = useLocation()
  if (status === 'unauthorized') {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  return <>{children}</>
}

export default AuthContext
